import React, { Dispatch, useContext, useRef, useState } from 'react';
import { IGif } from '@giphy/js-types';
import {
  Grid as GiphyGrid,
  SearchBar,
  SearchContext,
  SearchContextManager,
} from '@giphy/react-components';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import GifIcon from '@mui/icons-material/Gif';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import { Avatar, Box, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Editor } from '@tinymce/tinymce-react';
import { Formik, useFormikContext } from 'formik';
import { useLocation } from 'react-router-dom';
import { useCreateFeedThread, useUpdateThread } from '@juno/client-api';
import { PreviewFile } from '@juno/client-api/fakeModel';
import { CommentMediaTypeEnum, CommunityUser, Thread, ThreadBody } from '@juno/client-api/model';
import { DrawerFooter, MediaPreview, VisuallyHiddenInput } from '@juno/ui';
import {
  FEATURE_FLAG,
  MutationAction,
  onMutation,
  uploadImagesToCloudinary,
  useFeatureFlagWithUser,
  useSettings,
  validateSelectedFileSize,
} from '@juno/utils';
import { createTinyMceMentionSpan, mentions_fetch } from '../helpers';

const ACCEPTED_MEDIA_TYPES = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'image/tiff',
  'image/tif',
  'image/webp',
  'image/bmp',
  'image/svg',
];

interface CommentEditorProps {
  editorRef: React.MutableRefObject<any>;
  userMentions: React.MutableRefObject<CommunityUser[]>;
  thread?: Thread;
  parentThreadId: string;
  showAvatar?: boolean;
  onCancel?: () => void;
  handleRefetch?: () => void;
  onSubmitSuccess?: () => void;
  confirmButtonText?: string;
  isReply?: boolean;
}

interface CommentEditorInnerProps extends CommentEditorProps {
  previewMedia?: PreviewFile | null;
  setPreviewMedia: Dispatch<React.SetStateAction<PreviewFile | null>>;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
}

const CommentEditorInner: React.FC<CommentEditorInnerProps> = ({
  editorRef,
  userMentions,
  thread,
  onCancel = () => {},
  showAvatar,
  previewMedia,
  setPreviewMedia,
  fileInputRef,
  confirmButtonText = 'Comment',
  isReply = false,
}) => {
  const theme = useTheme();
  const { getImageSrc, platform, user } = useSettings();
  const [giphyOpen, setGiphyOpen] = useState(false);
  const giphyAnchorEl = useRef<HTMLButtonElement | null>(null);
  const { values, setFieldValue, handleSubmit, dirty, isSubmitting } = useFormikContext<Thread>();
  const [isFocused, setIsFocused] = useState(editorRef.current?.hasFocus() || false);

  const { value: giphyEnabled } = useFeatureFlagWithUser(FEATURE_FLAG.GIPHY);

  const editorInputSx = {
    border: '2px solid',
    outline: `1px solid ${theme.palette.grey[400]}`,
    borderColor: isFocused ? theme.palette.primary.main : 'transparent',
    borderRadius: '8px',
    boxShadow: isFocused ? theme.shadows[1] : 'none',
    '&:hover': {
      outline: isFocused ? 'none' : `1px solid ${theme.palette.grey[700]}`,
      boxShadow: theme.shadows[1],
    },
  };

  return (
    <Box gap={2} display='flex'>
      {showAvatar && (
        <Avatar
          src={user?.avatar || user?.icon || ''}
          alt={`${user?.first_name} ${user?.last_name}`}
        />
      )}
      <Box flex={1}>
        <Box position={'relative'} sx={editorInputSx} minHeight={isReply ? 80 : 150}>
          <Editor
            apiKey={process.env.NX_TINY_MCE_API_KEY}
            onInit={(evt, editor) => {
              editorRef.current = editor;
              setIsFocused(editor.hasFocus());
            }}
            initialValue={thread ? (thread.body as unknown as string) : ''}
            inline={true}
            onEditorChange={(value) => {
              setFieldValue('body', value);
            }}
            init={{
              convert_urls: false,
              contextmenu: false,
              placeholder: isReply ? 'Add Reply' : 'Add Comment',
              init_instance_callback: function (editor) {
                editor.focus(); // Focus the editor
                editor.selection.select(editor.getBody(), true); // Select all content in the editor
                editor.selection.collapse(false); // Collapse the selection to the end
              },
              content_css: '/styles.css',
              content_style: `.user_mention{ color: ${
                theme.palette.primary.main
              };, cursor: pointer; } .mce-content-body {
          padding: 12px;
          padding-bottom: 36px!important;
          margin: 0;
          font-size: 14px;
          min-height: ${isReply ? '80px' : '150px'};
          width: 100%; 
          box-sizing: border-box;
          line-height: 26px;
        }
        .mce-content-body:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        .mce-content-body p { margin: 0; }`,
              mentions_selector: 'span.user_mention',
              mentions_menu_complete: (editor: any, userInfo: any) => {
                userMentions.current = [...userMentions.current, userInfo];
                const span = createTinyMceMentionSpan(editor, userInfo);
                return span;
              },
              mentions_fetch: (query: any, success: any) => {
                mentions_fetch(query, success, getImageSrc, platform?.id || '');
              },
              mentions_item_type: 'profile',
              width: '100%',
              setup: (editor) => {
                editor.on('focus', () => setIsFocused(true));
                editor.on('blur', () => setIsFocused(false));
              },
              toolbar: false,
              menubar: false,
              statusbar: false,
              autoresize: true,
            }}
            plugins={'autolink emoticons link mentions'}
          />
          <Stack direction='row' position={'absolute'} bottom={5} left={10} zIndex={'10'}>
            <Tooltip title='Insert Emoji' placement='top'>
              <IconButton
                size='small'
                onClick={() => {
                  editorRef.current?.execCommand('mceEmoticons');
                }}
                aria-label='Insert emoji'
              >
                <EmojiEmotionsOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Attach Image' placement='top'>
              <IconButton
                size='small'
                component='label'
                aria-label='Attach Photo'
                disabled={!!values.comment_media_type}
              >
                <VisuallyHiddenInput
                  type='file'
                  // TODO - need to remove this once we get videos working on the app
                  accept={'image/*'}
                  ref={fileInputRef}
                  onChange={(event: any) => {
                    const file = event.target.files[0] as PreviewFile;
                    if (file && ACCEPTED_MEDIA_TYPES.includes(file.type)) {
                      if (validateSelectedFileSize(file)) {
                        file.preview = URL.createObjectURL(file);
                        setPreviewMedia(file);
                        setFieldValue('comment_media_type', CommentMediaTypeEnum.image);
                      }
                    } else {
                      alert('File type not supported');
                    }
                  }}
                />
                <InsertPhotoOutlinedIcon />
              </IconButton>
            </Tooltip>
            {giphyEnabled && (
              <Tooltip title='Attach GIF' placement='top'>
                <IconButton
                  size='small'
                  aria-label='Attach GIF'
                  onClick={() => setGiphyOpen((old) => !old)}
                  ref={giphyAnchorEl}
                  disabled={!!values.comment_media_type}
                >
                  <GifIcon />
                </IconButton>
              </Tooltip>
            )}
            {/* was testing programattic mce commands, might come back to this so leaving it in
             
            {!isReply && (
              <>
                <Tooltip title='Underline' placement='top'>
                  <IconButton
                    size='small'
                    aria-label='Underline'
                    onClick={() => editorRef.current?.execCommand('mceUnderline')}
                  >
                    <FormatUnderlinedIcon />
                  </IconButton>
                </Tooltip>
              </>
            )} */}

            <Tooltip title='Insert Link' placement='top'>
              <IconButton
                aria-label='Insert link'
                size='small'
                onClick={() => {
                  editorRef.current?.execCommand('mceLink');
                }}
              >
                <InsertLinkOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Popover
            id={'giphy-popover'}
            open={giphyOpen}
            anchorEl={giphyAnchorEl.current}
            onClose={() => setGiphyOpen(false)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            sx={{ '.MuiPaper-root': { height: 300, p: 1 } }}
          >
            {' '}
            <Stack
              direction='row'
              alignItems={'center'}
              justifyContent={'center'}
              spacing={1}
              bottom={0}
              left={0}
              width={'100%'}
              bgcolor='#fff'
            >
              <Typography>Powered by </Typography>
              <img
                src='https://images.icon-icons.com/2699/PNG/512/giphy_logo_icon_168175.png'
                alt='dlLogo.png'
                width='66'
                height='auto'
              ></img>
            </Stack>
            <SearchExperience
              apiKey={process.env.NX_GIPHY_API_KEY || ''}
              onGifClick={(gif) => {
                setGiphyOpen(false);
                setFieldValue('comment_media_type', CommentMediaTypeEnum.gif);
                setFieldValue('comment_media', gif.id as string);
              }}
            />
          </Popover>
        </Box>
        {values.comment_media_type && (
          <Box mt={2}>
            <MediaPreview
              onDelete={() => {
                setFieldValue('comment_media_type', null);
                setFieldValue('comment_media', null);
                setPreviewMedia(null);
              }}
              gifId={values.comment_media || undefined}
              type={values.comment_media_type}
              url={previewMedia?.preview || values.comment_media || ''}
            />
          </Box>
        )}
        <DrawerFooter
          submitting={isSubmitting}
          disabled={!dirty || (!editorRef.current?.getContent() && !values.comment_media_type)}
          onCancel={onCancel}
          confirmButtonText={values.id ? 'Save' : confirmButtonText}
          onSubmit={async () => handleSubmit()}
        />
      </Box>
    </Box>
  );
};

const CommentEditor: React.FC<CommentEditorProps> = (props) => {
  const location = useLocation();
  const { editorRef, userMentions, thread, parentThreadId, handleRefetch, onSubmitSuccess } = props;
  const { sitePath, siteId, user } = useSettings();
  const [previewMedia, setPreviewMedia] = useState<PreviewFile | null>(null);
  const threadCreate = useCreateFeedThread(onMutation(MutationAction.CREATE, '', handleRefetch));
  const threadUpdate = useUpdateThread(onMutation(MutationAction.UPDATE, '', handleRefetch));
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Formik
      initialValues={thread || ({} as Thread)}
      enableReinitialize
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        // double check that the user mentions are in the body
        const taggedUsers = userMentions.current?.filter((m) =>
          editorRef.current?.getContent().includes(m.id),
        );
        // split on the sitePath/ to grab the page slug and the possible discussion id
        const pageSlug = location.pathname.split(`${sitePath}/`).pop() || '';

        let data = {} as Thread;
        if (!values.id) {
          data = {
            body: (editorRef.current?.getContent() as unknown as ThreadBody) || '<span></span>',
            author_id: user?.id,
            parent_thread_id: thread?.parent_thread_id || parentThreadId,
            tagged_users: taggedUsers,
          } as Thread;
        } else {
          data = {
            body: (editorRef.current?.getContent() as unknown as ThreadBody) || '<span></span>',
            tagged_users: taggedUsers,
          } as Thread;
        }

        if (values.comment_media_type === CommentMediaTypeEnum.image && previewMedia) {
          try {
            const newImage = await uploadImagesToCloudinary([previewMedia], () => {});

            data.comment_media = newImage.url;
            data.comment_media_type = CommentMediaTypeEnum.image;
          } catch (e) {
            console.error(e);
            return;
          }

          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }

        const newThread = { ...values, ...data } as Thread;

        if (!values.id) {
          try {
            const nt = await threadCreate.mutateAsync({
              siteId: siteId,
              feedId: parentThreadId || thread?.id || '',
              data: {
                ...newThread,
                metadata: {
                  page_slug: pageSlug,
                },
              },
            });
            setPreviewMedia(null);
            onSubmitSuccess && onSubmitSuccess();
            resetForm();
          } catch (e) {
            console.error(e);
          } finally {
            setSubmitting(false);
          }
        } else {
          try {
            const nt = await threadUpdate.mutateAsync({
              siteId: siteId,
              threadId: values.id,
              data: {
                ...newThread,
                metadata: {
                  page_slug: pageSlug,
                },
              },
            });
            setPreviewMedia(null);
            onSubmitSuccess && onSubmitSuccess();
            resetForm();
          } catch (e) {
            console.error(e);
          } finally {
            setSubmitting(false);
          }
        }
      }}
    >
      <CommentEditorInner
        {...props}
        fileInputRef={fileInputRef}
        previewMedia={previewMedia}
        setPreviewMedia={setPreviewMedia}
      />
    </Formik>
  );
};

export default CommentEditor;

interface SearchExperienceProps {
  apiKey: string;
  onGifClick: (gif: IGif) => void;
}
// the search experience consists of the manager and its child components that use SearchContext
export const SearchExperience: React.FC<SearchExperienceProps> = ({ apiKey = '', onGifClick }) => (
  <SearchContextManager apiKey={apiKey}>
    <SearchComponents onGifClick={onGifClick} />
  </SearchContextManager>
);
interface SearchComponentsProps {
  onGifClick: (gif: IGif) => void;
}
const SearchComponents: React.FC<SearchComponentsProps> = ({ onGifClick }) => {
  const { fetchGifs, searchKey } = useContext(SearchContext);
  return (
    <Box pb={3} position='relative'>
      <SearchBar />
      <Box p={1} />
      <GiphyGrid
        onGifClick={(gif, e) => {
          e.preventDefault();
          e.stopPropagation();
          onGifClick(gif);
        }}
        fetchGifs={fetchGifs}
        width={240}
        columns={3}
        gutter={6}
        key={searchKey}
        hideAttribution
      />
    </Box>
  );
};
